import React, { useState, useMemo } from 'react';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';

/**
 * Compact multi-select with optional search box. Stores the selection as an
 * array of value strings; emit via `onChange(string[])`. The parent is
 * responsible for joining with "," when sending to the API.
 *
 * Props:
 *   options:  [{ value: string, label: string }]
 *   value:    string[]  (current selection)
 *   onChange: (string[]) => void
 *   placeholder: string  (button label when empty)
 *   searchable: boolean  (default true — show search input)
 *   dataTestid:  string
 *   width:    string css (default 'w-44')
 */
export function MultiSelectFilter({
  options = [],
  value = [],
  onChange,
  placeholder = 'Todos',
  searchable = true,
  dataTestid,
  width = 'w-44',
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const filtered = useMemo(() => {
    if (!q.trim()) return options;
    const needle = q.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(needle));
  }, [q, options]);

  const toggle = (v) => {
    const has = value.includes(v);
    onChange(has ? value.filter((x) => x !== v) : [...value, v]);
  };

  const summary = (() => {
    if (!value.length) return placeholder;
    if (value.length === 1) {
      const opt = options.find((o) => o.value === value[0]);
      return opt?.label || value[0];
    }
    return `${value.length} seleccionados`;
  })();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={`h-8 text-xs justify-between ${width} font-normal`}
          data-testid={dataTestid}
        >
          <span className={value.length ? '' : 'opacity-60'}>{summary}</span>
          <ChevronsUpDown size={11} className="opacity-50 shrink-0 ml-1" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-64" align="start" style={{ background: 'var(--vz-bg-card)', borderColor: 'var(--vz-border-subtle)' }}>
        {searchable && (
          <div className="p-2 border-b" style={{ borderColor: 'var(--vz-border-subtle)' }}>
            <input
              type="text"
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar..."
              className="h-7 text-xs px-2 w-full rounded bg-transparent border outline-none focus:ring-1"
              style={{ borderColor: 'var(--vz-border-subtle)', color: 'var(--vz-text-primary)' }}
              data-testid={dataTestid ? `${dataTestid}-search` : undefined}
            />
          </div>
        )}
        <div className="max-h-72 overflow-y-auto p-1">
          {filtered.length === 0 ? (
            <div className="text-xs text-center py-4" style={{ color: 'var(--vz-text-muted)' }}>
              Sin resultados
            </div>
          ) : filtered.map((opt) => {
            const sel = value.includes(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => toggle(opt.value)}
                className="w-full flex items-center gap-2 px-2 py-1.5 text-xs rounded hover:bg-black/10"
                style={{ color: sel ? 'var(--vz-text-primary)' : 'var(--vz-text-secondary)' }}
                data-testid={dataTestid ? `${dataTestid}-opt-${opt.value}` : undefined}
              >
                <span
                  className="inline-flex items-center justify-center w-3.5 h-3.5 rounded border shrink-0"
                  style={{
                    background: sel ? 'var(--vz-accent, #4A9EFF)' : 'transparent',
                    borderColor: sel ? 'var(--vz-accent, #4A9EFF)' : 'var(--vz-border-subtle)',
                  }}
                >
                  {sel && <Check size={9} style={{ color: 'white' }} strokeWidth={3} />}
                </span>
                <span className="truncate flex-1 text-left" title={opt.label}>{opt.label}</span>
              </button>
            );
          })}
        </div>
        {value.length > 0 && (
          <div className="p-2 border-t flex items-center justify-between" style={{ borderColor: 'var(--vz-border-subtle)' }}>
            <span className="text-[10px]" style={{ color: 'var(--vz-text-muted)' }}>{value.length} sel.</span>
            <button
              type="button"
              onClick={() => onChange([])}
              className="text-[10px] flex items-center gap-1 hover:underline"
              style={{ color: 'var(--vz-text-muted)' }}
              data-testid={dataTestid ? `${dataTestid}-clear` : undefined}
            >
              <X size={10} /> Limpiar
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
